import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, FileText, Loader2 } from "lucide-react";

interface ImportBatch {
  import_batch_id: string;
  imported_at: string;
  task_count: number;
  event_count: number;
  week_start: string | null;
  source?: "school_plan_import" | "spond_import";
}

interface Props {
  open: boolean;
  onClose: () => void;
  childId: string;
  childName: string;
}

export function ImportHistorySheet({ open, onClose, childId, childName }: Props) {
  const qc = useQueryClient();
  const [confirmBatchId, setConfirmBatchId] = useState<string | null>(null);

  const { data: batches = [], isLoading } = useQuery({
    queryKey: ["import-batches", childId],
    queryFn: async () => {
      // Get batches from calendar events
      const { data: events } = await supabase
        .from("child_calendar_events" as any)
        .select("import_batch_id, created_at, specific_date, source")
        .eq("child_id", childId)
        .in("source", ["school_plan_import", "spond_import"])
        .not("import_batch_id", "is", null)
        .order("created_at", { ascending: false });

      // Get batches from tasks
      const { data: tasks } = await supabase
        .from("task_items" as any)
        .select("import_batch_id, created_at, date")
        .eq("child_id", childId)
        .not("import_batch_id", "is", null)
        .order("created_at", { ascending: false });

      // Get batches from homework
      const { data: homework } = await supabase
        .from("homework" as any)
        .select("import_batch_id, created_at, week_number")
        .eq("child_id", childId)
        .not("import_batch_id", "is", null)
        .order("created_at", { ascending: false });

      // Group by batch_id
      const batchMap = new Map<string, ImportBatch>();

      for (const e of (events || []) as any[]) {
        if (!e.import_batch_id) continue;
        const existing = batchMap.get(e.import_batch_id);
        if (existing) {
          existing.event_count++;
          if (e.specific_date && (!existing.week_start || e.specific_date < existing.week_start)) {
            existing.week_start = e.specific_date;
          }
        } else {
          batchMap.set(e.import_batch_id, {
            import_batch_id: e.import_batch_id,
            imported_at: e.created_at,
            task_count: 0,
            event_count: 1,
            week_start: e.specific_date || null,
            source: e.source === "spond_import" ? "spond_import" : "school_plan_import",
          });
        }
      }

      for (const t of (tasks || []) as any[]) {
        if (!t.import_batch_id) continue;
        const existing = batchMap.get(t.import_batch_id);
        if (existing) {
          existing.task_count++;
          if (t.date && (!existing.week_start || t.date < existing.week_start)) {
            existing.week_start = t.date;
          }
        } else {
          batchMap.set(t.import_batch_id, {
            import_batch_id: t.import_batch_id,
            imported_at: t.created_at,
            task_count: 1,
            event_count: 0,
            week_start: t.date || null,
          });
        }
      }

      for (const h of (homework || []) as any[]) {
        if (!h.import_batch_id) continue;
        const existing = batchMap.get(h.import_batch_id);
        if (existing) {
          existing.task_count++; // count homework as tasks for display
        } else {
          batchMap.set(h.import_batch_id, {
            import_batch_id: h.import_batch_id,
            imported_at: h.created_at,
            task_count: 1,
            event_count: 0,
            week_start: null,
          });
        }
      }

      return Array.from(batchMap.values()).sort(
        (a, b) => new Date(b.imported_at).getTime() - new Date(a.imported_at).getTime()
      );
    },
    enabled: open && !!childId,
  });

  const deleteBatch = useMutation({
    mutationFn: async (batchId: string) => {
      // 1. Delete homework_subtasks for homework in this batch first (no cascade)
      const { data: hwRows } = await supabase
        .from("homework" as any)
        .select("id")
        .eq("child_id", childId)
        .eq("import_batch_id", batchId);
      const hwIds = (hwRows || []).map((h: any) => h.id);
      if (hwIds.length > 0) {
        const { error: subErr } = await supabase
          .from("homework_subtasks" as any)
          .delete()
          .in("homework_id", hwIds);
        if (subErr) throw subErr;
      }

      // 2. Delete reminder_occurrences for reminders in this batch
      const { data: remRows } = await supabase
        .from("reminders" as any)
        .select("id")
        .eq("child_id", childId)
        .eq("import_batch_id", batchId);
      const remIds = (remRows || []).map((r: any) => r.id);
      if (remIds.length > 0) {
        const { error: roErr } = await supabase
          .from("reminder_occurrences" as any)
          .delete()
          .in("reminder_id", remIds);
        if (roErr) throw roErr;
      }

      // 3. Delete main rows in parallel
      const [evRes, taskRes, hwRes, remRes, spiRes] = await Promise.all([
        supabase
          .from("child_calendar_events" as any)
          .delete()
          .eq("child_id", childId)
          .eq("import_batch_id", batchId),
        supabase
          .from("task_items" as any)
          .delete()
          .eq("child_id", childId)
          .eq("import_batch_id", batchId),
        supabase
          .from("homework" as any)
          .delete()
          .eq("child_id", childId)
          .eq("import_batch_id", batchId),
        supabase
          .from("reminders" as any)
          .delete()
          .eq("child_id", childId)
          .eq("import_batch_id", batchId),
        supabase
          .from("school_plan_imports" as any)
          .delete()
          .eq("child_id", childId)
          .eq("import_batch_id", batchId),
      ]);
      if (evRes.error) throw evRes.error;
      if (taskRes.error) throw taskRes.error;
      if (hwRes.error) throw hwRes.error;
      if (remRes.error) throw remRes.error;
      if (spiRes.error) throw spiRes.error;
    },
    onSuccess: () => {
      toast.success("Import angret – alle oppgaver og hendelser er slettet");
      qc.invalidateQueries({ queryKey: ["import-batches", childId] });
      qc.invalidateQueries({ queryKey: ["child-calendar-events", childId] });
      qc.invalidateQueries({ queryKey: ["task-items"] });
      qc.invalidateQueries({ queryKey: ["homework", childId] });
      qc.invalidateQueries({ queryKey: ["reminders", childId] });
      setConfirmBatchId(null);
    },
    onError: () => {
      toast.error("Noe gikk galt ved sletting");
    },
  });

  function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString("nb-NO", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function formatWeek(dateStr: string | null) {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    const endOfWeek = new Date(d);
    endOfWeek.setDate(d.getDate() + 4);
    return `${d.getDate()}.–${endOfWeek.getDate()}. ${d.toLocaleDateString("nb-NO", { month: "short" })}`;
  }

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[70vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Importerte ukeplaner</SheetTitle>
            <SheetDescription>
              Oversikt over importerte ukeplaner for {childName}. Du kan angre en import ved å slette alt den opprettet.
            </SheetDescription>
          </SheetHeader>

          <div className="py-4 space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Laster…
              </div>
            ) : batches.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Ingen importerte ukeplaner funnet
              </p>
            ) : (
              batches.map((batch) => (
                <div
                  key={batch.import_batch_id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">
                        {batch.source === "spond_import" ? "Spond" : "Ukeplan"} {batch.source !== "spond_import" && formatWeek(batch.week_start)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Importert {formatDate(batch.imported_at)}
                      </p>
                      <div className="flex gap-1.5 mt-1">
                        {batch.task_count > 0 && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {batch.task_count} oppgaver
                          </Badge>
                        )}
                        {batch.event_count > 0 && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {batch.event_count} hendelser
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                    onClick={() => setConfirmBatchId(batch.import_batch_id)}
                    disabled={deleteBatch.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirmBatchId} onOpenChange={(o) => !o && setConfirmBatchId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Angre import?</AlertDialogTitle>
            <AlertDialogDescription>
              Dette sletter alle oppgaver og kalenderhendelser som ble opprettet av denne importen. Handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmBatchId && deleteBatch.mutate(confirmBatchId)}
            >
              {deleteBatch.isPending ? "Sletter…" : "Slett alt"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
